import {
  ColumnShadow,
  ColumnWidth,
  IGroupingStatuses,
  ITableConfig,
  ITableConfigValueColumn,
  Label,
} from '../../models/tableConfig';
import { classNames } from '../../utils/classNames';
import styles from './AceTable.module.css';
import { ReactNode, useRef, useState } from 'react';
import {
  Classes,
  Collapse,
  NonIdealState,
  PopoverInteractionKind,
  Tooltip,
} from '@blueprintjs/core';
import React from 'react';
import { LoanQuoteRequirements } from '../BankPanel/BankPanels/LoanQuoteRequirements/LoanQuoteRequirements';
import DynamicRenderer, {
  DynamicComponentPropTypes,
  DynamicComponentTypes,
} from '../BankPanel/BankPanels/LoanQuoteRequirements/DynamicRenderer';

function HeaderCell({
  label,
  labelIcon,
  width,
  className,
  sticky,
  shadow,
}: {
  label?: Label;
  labelIcon?: ReactNode;
  width?: ColumnWidth;
  className?: string;
  sticky?: boolean;
  shadow?: ColumnShadow;
}) {
  return (
    <th
      className={classNames(
        styles.header,
        sticky && styles.isSticky,
        shadow && styles.hasShadow,
        shadow && styles[shadow],
        styles[width ?? ''],
        className
      )}
    >
      <Tooltip
        interactionKind={PopoverInteractionKind.HOVER}
        content={typeof label === 'string' ? label : label?.label}
        placement="top"
        hoverCloseDelay={200}
        className={styles.headerLabelWrapper}
      >
        <span className={styles.headerLabel}>
          <span className={styles.headerLabelText}>
            {typeof label === 'string' ? label : label?.content}
          </span>
          {labelIcon && (
            <span className={styles.haederLabelIcon}>{labelIcon}</span>
          )}
        </span>
      </Tooltip>
    </th>
  );
}

function Cell({
  children,
  truncate,
  width,
  className,
  sticky,
  shadow,
}: {
  children?: ReactNode;
  truncate?: boolean;
  width?: ColumnWidth;
  className?: string;
  sticky?: boolean;
  shadow?: ColumnShadow;
}) {
  return (
    <td
      className={classNames(
        styles.cell,
        sticky && styles.isSticky,
        shadow && styles.hasShadow,
        shadow && styles[shadow],
        styles[width ?? ''],
        className
      )}
    >
      <div className={classNames(truncate && styles.truncate)}>{children}</div>
    </td>
  );
}

export interface IAceTable<T> {
  config: ITableConfig<T>;
  data: T[];
  maxRows?: number;
  uniqueRowIDKey: keyof T;
  groupingKeys?: (keyof T)[];
  detailsKey?: keyof T;
  rowClassName?: (item: T) => string;
  loading?: boolean;
  error?: string;
}

export function AceTable<T>({
  config,
  data,
  maxRows,
  uniqueRowIDKey,
  groupingKeys,
  detailsKey,
  rowClassName,
  loading,
  error,
}: IAceTable<T>) {
  groupingKeys = groupingKeys ?? [];

  const visibleRows = maxRows
    ? { maxHeight: (maxRows * 3).toString() + 'rem', overflow: 'auto' }
    : undefined;

  const entries = Object.entries(config.columns) as [
    Extract<keyof T, string>,
    ITableConfigValueColumn<T> | undefined
  ][];

  const [collapsibleRow, setCollapsibleRow] = useState<Record<string, boolean>>(
    {}
  );

  const rowRefs = useRef<Map<string, HTMLTableRowElement | null>>(new Map());

  const toggleRowDetails = (
    e: React.MouseEvent<HTMLTableRowElement, MouseEvent>,
    rowId: string
  ) => {
    const ref = rowRefs.current.get(rowId);
    if (ref === e.currentTarget) {
      setCollapsibleRow((prev) => ({
        ...prev,
        [rowId]: !prev[rowId],
      }));
    }
  };

  const dynamicComponents = {
    loanQuoteRequirements: LoanQuoteRequirements,
  };

  return (
    <div style={visibleRows}>
      <table className={styles.table}>
        <thead className={styles.headerSection}>
          <tr>
            {entries.map(([key, spec]) => (
              <HeaderCell
                key={key}
                label={spec?.label}
                labelIcon={spec?.labelIcon}
                width={spec?.width}
                className={spec?.className}
                sticky={spec?.sticky}
                shadow={spec?.shadow}
              />
            ))}
            {config.extra?.map((spec) => (
              <HeaderCell
                key={spec.key}
                label={spec.label}
                labelIcon={spec?.labelIcon}
                width={spec.width}
                className={spec?.className}
                sticky={spec.sticky}
                shadow={spec.shadow}
              />
            ))}
          </tr>
        </thead>
        <tbody>
          {!data.length && !loading && (
            <tr className={styles.row}>
              <td colSpan={entries.length + (config.extra?.length ?? 0)}>
                <NonIdealState icon="blank">
                  {error || 'No results'}
                </NonIdealState>
              </td>
            </tr>
          )}
          {!data.length && loading && (
            <tr className={classNames(styles.row)}>
              {entries.map(([key, spec]) => (
                <Cell
                  key={`${key}.header`}
                  className={classNames(
                    Classes.SKELETON,
                    styles.skeleton,
                    spec?.className
                  )}
                  width={spec?.width}
                  sticky={spec?.sticky}
                  shadow={spec?.shadow}
                />
              ))}
              {config.extra?.map((spec) => (
                <Cell
                  key={spec.key}
                  className={classNames(
                    Classes.SKELETON,
                    styles.skeleton,
                    spec.className
                  )}
                  width={spec.width}
                  sticky={spec.sticky}
                  shadow={spec.shadow}
                />
              ))}
            </tr>
          )}
          {data.map((rowItem, i) => {
            const groupingStatuses = getGroupingStatus(groupingKeys, data, i);
            return (
              <React.Fragment
                key={rowItem[uniqueRowIDKey]?.toString() ?? i.toString()}
              >
                <tr
                  className={classNames(
                    styles.row,
                    rowClassName && rowClassName(rowItem),
                    `${
                      detailsKey && rowItem[detailsKey]
                        ? styles.clickableRow
                        : ''
                    }`,
                    `${
                      detailsKey &&
                      collapsibleRow[rowItem[uniqueRowIDKey] as string]
                        ? styles.clickableRowExpanded
                        : ''
                    }`
                  )}
                  ref={(el) => {
                    rowRefs.current.set(rowItem[uniqueRowIDKey] as string, el);
                  }}
                  onClick={(e) =>
                    detailsKey && rowItem[detailsKey]
                      ? toggleRowDetails(e, rowItem[uniqueRowIDKey] as string)
                      : undefined
                  }
                >
                  {entries.map(([key, spec]) => (
                    <Cell
                      key={rowItem[uniqueRowIDKey] + key}
                      truncate={!spec?.noTruncate}
                      width={spec?.width}
                      className={spec?.className}
                      sticky={spec?.sticky}
                      shadow={spec?.shadow}
                    >
                      {spec?.render(rowItem[key], rowItem, groupingStatuses)}
                    </Cell>
                  ))}
                  {config.extra?.map((spec) => (
                    <Cell
                      key={rowItem[uniqueRowIDKey] + spec.key}
                      truncate={!spec.noTruncate}
                      width={spec.width}
                      className={spec.className}
                      sticky={spec.sticky}
                      shadow={spec.shadow}
                    >
                      {spec.render(rowItem, groupingStatuses)}
                    </Cell>
                  ))}
                </tr>
                {collapsibleRow[rowItem[uniqueRowIDKey] as string] && (
                  <tr className={styles.dynamicRow}>
                    <td colSpan={12}>
                      <Collapse
                        isOpen={
                          collapsibleRow[rowItem[uniqueRowIDKey] as string]
                        }
                        keepChildrenMounted={true}
                      >
                        {detailsKey && (
                          <DynamicRenderer
                            componentName={detailsKey as DynamicComponentTypes}
                            components={dynamicComponents}
                            componentProps={
                              rowItem[detailsKey] as DynamicComponentPropTypes
                            }
                          ></DynamicRenderer>
                        )}
                      </Collapse>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// Checks if element is part of a group, as defined by the provided groupingKey,
// and if it is the first or last item in that group.
function getGroupingStatus<T>(
  groupingKeys: (keyof T)[],
  data: T[],
  index: number
): IGroupingStatuses {
  const element = data[index];
  const hasNextElement = index < data.length - 1;
  const hasPreviousElement = index > 0;

  return groupingKeys.reduce<IGroupingStatuses>(
    (groupingStatuses, groupingKey) => {
      let isInGroup = false;
      let isFirstInGroup = false;
      let isLastInGroup = false;

      if (!groupingKey || !element[groupingKey] || data.length <= 1) {
        groupingStatuses = {
          ...groupingStatuses,
          [groupingKey]: {
            isInGroup,
            isFirstInGroup,
            isLastInGroup,
          },
        };
        return groupingStatuses;
      }

      if (!isInGroup && hasNextElement) {
        const nextElement = data[index + 1];
        isInGroup = element[groupingKey] === nextElement[groupingKey];
      }

      if (!isInGroup && hasPreviousElement) {
        const previousElement = data[index - 1];
        isInGroup = element[groupingKey] === previousElement[groupingKey];
      }

      if (isInGroup) {
        isFirstInGroup =
          !hasPreviousElement ||
          element[groupingKey] !== data[index - 1][groupingKey];
        isLastInGroup =
          !hasNextElement ||
          element[groupingKey] !== data[index + 1][groupingKey];
      }

      groupingStatuses = {
        ...groupingStatuses,
        [groupingKey]: {
          isInGroup,
          isFirstInGroup,
          isLastInGroup,
        },
      };
      return groupingStatuses;
    },
    {}
  );
}
